import { landmineNames } from '@mm/constants/landmine-names';
import {
  BasicPage,
  PageBillingType,
} from '@mm/state/interfaces/connected-page';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import {
  selectAvailablePages,
  selectBasicPages,
} from '@mm/state/selectors/facebookPageSelector';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import {
  activeCompanySelector,
  selectCompanies,
} from '@mm/state/selectors/viewSelectors';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { ListGroupItem } from 'reactstrap';

import {
  isFacebookUserSelector,
  selectSessionSelector,
} from '@mm/state/selectors/userSelector';
import { useConnectedPageCRUDActions } from '@mm/state/actions/ConnectedPageCRUDActions';

import { PrimaryButton } from '../../../common/buttons/PrimaryButton/PrimaryButton';
import ConnectPagesArea from '../../../common/components/ConnectPagesArea/ConnectPagesArea';
import { PAGES_UPGRADE_COUNT } from '../../../common/constants/payments';
import { CHATBOT_EDITOR } from '../../../common/constants/routes';
import SwitchCompanyDropdown from '../common/SwitchCompanyDropdown/SwitchCompanyDropdown';
import './ConnectFBPageToCompanyPage.pcss';
import { ConnectBusinessButton } from '../../../layout/BotBuilderSidebar/ActivePageSlideMenu/PagesListHeader/PagesListHeader';
import UserDropdown from '../../../layout/BotBuilderSidebar/UserDropdown/UserDropdown';
import { useAmbassadorPaywall } from '../../../modules/LoginRouter/use-ambassador-paywall';

export const ConnectFBPageToCompanyPage = () => {
  const history = useHistory();

  const featureFlags = useSelector(combinedFeatureFlagsSelector);
  const availablePages = useSelector(selectAvailablePages);
  const companies = useSelector(selectCompanies);
  const basicPagesList = useSelector(selectBasicPages);
  const company = useSelector(activeCompanySelector);
  const isFacebookUser = useSelector(isFacebookUserSelector);
  const connectedPageCRUDActions = useConnectedPageCRUDActions();
  const session = useSelector(selectSessionSelector);

  const paywall = useAmbassadorPaywall();

  const modalActions = useModalActions();

  useEffect(() => {
    if (company) {
      connectedPageCRUDActions.retrieveBasicListByCompany(company.id);
    }
  }, [company]);

  if (!company) {
    return null;
  }

  const redirectToEditor = () => history.push(CHATBOT_EDITOR);

  const needsUpgrade =
    company?.editable &&
    company?.freePricePlan &&
    availablePages?.data.length > PAGES_UPGRADE_COUNT &&
    !featureFlags.connectPageUnlimitedPages;

  const afterPageConnect = () => {
    redirectToEditor();

    if (session.userAttributes.showPaywall) {
      paywall.openPaywall();
      return;
    }

    if (needsUpgrade) {
      modalActions.openUniversalUpgradeLandmineModal({
        landmineName: landmineNames['CONNECT-FB-PAGE-TO-COMPANY-PAGE'],
        feature: 'connectPageUnlimitedPages',
      });
    }
  };

  const createBot = (p: BasicPage) => {
    modalActions.openCreateBotModal(company.id, p.id);
  };

  const headerText = basicPagesList.length
    ? 'No Chatbots'
    : 'Connect a Facebook Page';
  const headerDescription = basicPagesList.length
    ? `None of the Facebook Pages connected to  ${company.name} have any chatbots.  Create a new chatbot for your pages to continue.`
    : `There are no Facebook Pages connected to ${company.name}. Switch to a different company or connect a Facebook Page below.`;

  return (
    <div className="connect-fb-page-to-company-page">
      <div className="connect-fb-page-to-company-page__header">
        <span className="connect-fb-page-to-company-page__menu">
          <UserDropdown />
        </span>

        <h1 className="connect-fb-page-to-company-page__header-text">
          {headerText}
        </h1>

        <div className="connect-fb-page-to-company-page__text">
          {headerDescription}
        </div>
      </div>

      {!!basicPagesList.length && (
        <div className="connect-fb-page-to-company-page__section-wrapper">
          <h3 className="connect-fb-page-to-company-page__section-header">
            Create New Bot
          </h3>

          {basicPagesList.map(p => (
            <ListGroupItem
              key={p.id}
              className="connect-fb-page-to-company-page__connected-page-table-item"
            >
              <div>{p.name}</div>
              <PrimaryButton
                className="connect-fb-page-to-company-page__create-bot-button"
                onClick={() => createBot(p)}
              >
                Create bot
              </PrimaryButton>
            </ListGroupItem>
          ))}
        </div>
      )}

      {companies.length >= 1 && (
        <div className="connect-fb-page-to-company-page__section-wrapper">
          <h3 className="connect-fb-page-to-company-page__section-header">
            Switch to a different company
          </h3>

          <SwitchCompanyDropdown
            className="connect-fb-page-to-company-page__switch-company-dropdown"
            companies={companies}
            showCreateCompany={false}
            afterCompanySwitch={redirectToEditor}
          />
        </div>
      )}

      {company && (
        <div className="connect-fb-page-to-company-page__section-wrapper">
          <h3 className="connect-fb-page-to-company-page__section-header">
            Connect a business
          </h3>

          <ConnectBusinessButton selectedCompany={company} />
        </div>
      )}

      {!basicPagesList.length && (
        <div className="connect-fb-page-to-company-page__section-wrapper">
          <h3 className="connect-fb-page-to-company-page__section-header">
            Or Connect a Facebook Page
          </h3>

          <ConnectPagesArea
            className="connect-fb-page-to-company-page__connect-area"
            afterPageConnect={afterPageConnect}
          />
        </div>
      )}
    </div>
  );
};
